import { inject, Injectable } from '@angular/core';
import {
  AndroidBiometryStrength,
  BiometricAuth,
  BiometryType,
  CheckBiometryResult
} from '@aparajita/capacitor-biometric-auth';
import { PluginListenerHandle } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject } from 'rxjs';

export interface BiometricLoginState {
  available: boolean;
  type: 'face' | 'finger' | 'biometric' | null;
  name: 'FaceID' | 'TouchID' | 'biometric' | null;
}

const initialState: BiometricLoginState = { available: false, type: null, name: null };

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {
  private readonly translateService: TranslateService = inject(TranslateService);

  private resumeHandle?: PluginListenerHandle;

  readonly state$ = new BehaviorSubject<BiometricLoginState>({
    ...initialState
  });

  async refreshAvailability(): Promise<BiometricLoginState> {
    const result = await BiometricAuth.checkBiometry();
    const state = this.mapAvailability(result);

    this.state$.next(state);
    return state;
  }

  async authenticate(): Promise<void> {
    await BiometricAuth.authenticate({
      reason: this.translateService.instant('SHARED.biometricLogin.allowQuestion'), // Description in biometric Prompt
      cancelTitle: this.translateService.instant('SHARED.actions.no'), // Title for cancel button on Android and IOS
      iosFallbackTitle: this.translateService.instant('SHARED.biometricLogin.fallback'), // Title of fallback button.
      androidTitle: this.translateService.instant('SHARED.biometricLogin.androidTitle'), // Title in biometric prompt (android only)
      androidSubtitle: this.translateService.instant('SHARED.biometricLogin.title'), // Subtitle in biometric Prompt (android only)
      androidConfirmationRequired: false,
      androidBiometryStrength: AndroidBiometryStrength.weak,
      allowDeviceCredential: false
    });
  }

  async startResumeTracking(): Promise<void> {
    if (this.resumeHandle) {
      return;
    }

    await this.refreshAvailability();

    this.resumeHandle = await BiometricAuth.addResumeListener((info: CheckBiometryResult) => {
      this.state$.next(this.mapAvailability(info));
    });
  }

  async stopResumeTracking(): Promise<void> {
    await this.resumeHandle?.remove();
    this.resumeHandle = undefined;
  }

  private mapAvailability(result: CheckBiometryResult): BiometricLoginState {
    if (!result.isAvailable) {
      return { ...initialState };
    }

    switch (result.biometryType) {
      case BiometryType.faceId:
        return { available: true, type: 'face', name: 'FaceID' };
      case BiometryType.touchId:
        return { available: true, type: 'finger', name: 'TouchID' };
      case BiometryType.fingerprintAuthentication:
      case BiometryType.faceAuthentication:
      case BiometryType.irisAuthentication:
      default:
        return { available: true, type: 'biometric', name: 'biometric' };
    }
  }
}
